import { createUniqueKey } from 'common/utils/strings';
import { LocationUrls } from 'common/types';
import {
	getPurchasedPlanPrice,
	Plan,
	PurchasedPlan,
	ProductPricing,
	getLocationPrice,
} from 'common/modules/plans';
import { getPricingString } from 'common/utils/common';
import i18n from 'services/localization/i18n';
import { ShopBilling } from 'common/modules/billing';
import { BILLING_CURRENCY } from 'common/modules/billing';

export const getUrlPathFromLocationName = (name: string): string => {
	const validChars = new RegExp(/[a-zA-Z0-9-]/);
	return name
		.trim()
		.toLowerCase()
		.split(' ')
		.join('')
		.split('')
		.map((char) => (validChars.test(char) ? char : ''))
		.join('');
};

export const createUniqueUrlPathFromLocationName = (
	locationUrls: LocationUrls,
	newLocationName: string,
): string => {
	const path = getUrlPathFromLocationName(newLocationName);
	return createUniqueKey(locationUrls, path);
};

export const getPlanInfo = (plan?: Plan): { name: string; description: string } => {
	switch (plan) {
		case 'CORE_COMMISSION':
		case 'CORE_FIXED':
		case 'CORE_DEMO':
		case 'CORE_INTERNAL_ONLY':
			return {
				name: 'CORE',
				description: i18n.t('shop.billingView.coreInfo', 'End-to-end solution for your operation.'),
			};
		case 'LITE':
			return {
				name: 'LITE',
				description: i18n.t(
					'shop.billingView.liteInfo',
					'Improve your online offering in minutes.',
				),
			};
		case 'ENTERPRISE':
			return {
				name: 'Enterprise',
				description: '',
			};
		case 'BASIC':
			return {
				name: 'Basic',
				description: '',
			};
		case 'GROW':
			return {
				name: 'Grow',
				description: '',
			};
		case 'ADVANCED':
			return {
				name: 'Advanced',
				description: '',
			};
		default:
			return {
				name: '',
				description: '',
			};
	}
};

export const getPlanPricingText = (plan?: PurchasedPlan, locationId?: string): string => {
	if (!plan) return '';

	switch (plan.plan) {
		case 'CORE_DEMO': {
			return i18n.t('common:billing.freeTrial', 'Free (trial)');
		}
		case 'LITE':
		case 'CORE_INTERNAL_ONLY':
		case 'ENTERPRISE': {
			return i18n.t('common:billing.free', 'Free');
		}
		case 'CORE_FIXED':
		case 'CORE_COMMISSION': {
			const price = getPurchasedPlanPrice(plan);
			if (!price || isFreePrice(price)) return i18n.t('common:billing.free', 'Free');

			if (plan.plan === 'CORE_COMMISSION') {
				const priceText = getFixedPlusPercentageText(price);

				return i18n.t('common:billing.pricePerBooking2', {
					price: priceText,
					defaultValue: '{{price}} / order',
				});
			} else {
				const priceText = getFixedPlusPercentageText(price);
				return i18n.t('common:billing.pricePerYear', {
					price: priceText,
					defaultValue: '{{price}} / year',
				});
			}
		}
		case 'BASIC':
		case 'GROW':
		case 'ADVANCED':
			const price = locationId ? getLocationPrice(plan, locationId) : getPurchasedPlanPrice(plan);
			if (!price || isFreePrice(price)) return i18n.t('common:billing.free', 'Free');
			return i18n.t('common:billing.pricePerMonth', {
				price: getPricingString(price.fixed!.value, BILLING_CURRENCY),
				defaultValue: '{{price}} / month',
			});
	}
};

export const getBillingCycleText = (shopBilling: ShopBilling | undefined, plan?: Plan): string => {
	if (shopBilling?.billingMethod === 'custom' || plan === 'CORE_FIXED') {
		return i18n.t('common:billing.billingCycleCustom', 'Custom');
	}

	return i18n.t('common:billing.billingCycleMonthly', 'Monthly');
};

export const isFreePrice = (price: ProductPricing) => {
	return !price.fixed?.value && !price.commission;
};

export const getFixedPlusPercentageText = (price: ProductPricing) => {
	const fixedPriceString = !!price.fixed?.value
		? getPricingString(price.fixed.value, BILLING_CURRENCY)
		: null;
	const percentagePriceString = !!price.commission ? `${price.commission}%` : null;

	return [fixedPriceString, percentagePriceString].filter((i) => !!i).join(' + ');
};
